import { Search } from "@mui/icons-material";
import { Grid, InputAdornment, Paper, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useFormik } from "formik";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { DEFAULT_ORGANIZATION } from "src/grading/models";
import { useAppSelector } from "src/store/hooks";
import { selectCurrentOrgID } from "src/store/reducers/auth/authSlice";
import { selectCurrentMembers } from "src/store/reducers/organizations/organizationSlice";
import {
  Member,
  useLazyGetMembersQuery,
  useLazyGetUnitMembersQuery,
} from "src/store/reducers/organizations/organizationsApiSlice";
import InviteMemberButton from "./InviteMemberButton/InviteMemberButton";
import InviteMemberModal from "./InviteMemberModal/InviteMemberModal";
import useStyles from "./MembersTab.styles";
import MembersTableColumns from "./MembersTableColumns/MembersTableColumns";

interface MembersTabProps {
  unitId?: string;
}

const MembersTab = ({ unitId }: MembersTabProps) => {
  const classes = useStyles();

  const [inviteMemberModalOpen, setInviteMemberModalOpen] = useState(false);

  const [memberGridRows, setMemberGridRows] = useState<Member[]>([]);

  const members = useAppSelector(selectCurrentMembers);

  const currentOrgID = useAppSelector(selectCurrentOrgID);

  const [getMembers, { isLoading }] = useLazyGetMembersQuery();

  const [getUnitMembers] = useLazyGetUnitMembersQuery();

  useEffect(() => {
    setMemberGridRows(members);
  }, [members]);

  useEffect(() => {
    const fetchMembers = async () => {
      if (unitId) {
        const { members } = await getUnitMembers({
          organizationId: currentOrgID ?? DEFAULT_ORGANIZATION.id,
          unitId,
        }).unwrap();

        setMemberGridRows(members.map((member) => ({ ...member })));
      }
    };

    fetchMembers();
  }, [getUnitMembers, unitId, currentOrgID]);

  const handleAddNewMember = () => {
    setInviteMemberModalOpen(true);
  };

  const formik = useFormik({
    initialValues: {
      search: "",
    },
    onSubmit: async (values) => {
      console.log("values", values);
    },
  });

  const { values, setFieldValue } = formik;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceSearch = useCallback(
    debounce((searchValue) => {
      if (!unitId) {
        getMembers({
          organizationId: currentOrgID ?? DEFAULT_ORGANIZATION.id,
          search: searchValue,
        })
          .unwrap()
          .then((response) => {
            setMemberGridRows(response.members);
          });
      } else {
        getUnitMembers({
          organizationId: currentOrgID ?? DEFAULT_ORGANIZATION.id,
          unitId,
          search: searchValue,
        })
          .unwrap()
          .then((response) => {
            setMemberGridRows(
              response.members.map((member) => ({
                ...member,
              })),
            );
          });
      }
    }, 500),
    [],
  );

  return isLoading ? (
    <Spinner
      animation="border"
      style={{ left: "50%", top: "50%", position: "relative" }}
    />
  ) : (
    <Grid item container>
      <Grid item container gap={2} className={classes.filtersContainer}>
        <TextField
          label="Search"
          name="search"
          type="text"
          value={values.search}
          onChange={(e) => {
            setFieldValue("search", e.target.value);
            debounceSearch(e.target.value);
          }}
          InputProps={{
            className: "text-field",
            endAdornment: (
              <InputAdornment position="end">
                <Search />
              </InputAdornment>
            ),
          }}
          placeholder="Search by user"
        />
        <InviteMemberButton onClick={handleAddNewMember} />
      </Grid>

      <Grid item container>
        <Paper className={classes.paperContainer}>
          <DataGrid
            rows={memberGridRows}
            hideFooterSelectedRowCount={true}
            headerHeight={50}
            pageSize={15}
            sx={{
              border: "none",
            }}
            rowHeight={108}
            className={classes.root}
            autoHeight
            columns={MembersTableColumns(setMemberGridRows)}
            getRowId={(row) => row.memberId}
          />
        </Paper>
      </Grid>

      <InviteMemberModal
        onClose={() => setInviteMemberModalOpen(false)}
        open={inviteMemberModalOpen}
      />
    </Grid>
  );
};

export default MembersTab;
